import { createRef, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { MessageToast } from '.';
import { useWritingModeContext, WritingModeProvider } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { ThemeProvider, useGlobalDesignTokens, useThemeContext } from '../../theme';
import { useDirectSpeech } from '../DirectSpeech';
import { getGlobalSettingsService, GlobalSettingsKeys } from '../Settings';
import bundle from './NovaComponents-MessageToast-gui-icu.properties';
import cn from './MessageToaster.css.json';
import './MessageToaster.css';
import { jsx as _jsx } from "react/jsx-runtime";
let openToast = null;
let toastId = 0;
const DEFAULT_DURATION = 5;

// There needs to be a discussion of how pop over content is handled globally.
// For now, we make sure there is some place to put the toasts.
function getStaticArea() {
  let staticArea = document.getElementById('static-area');
  if (!staticArea) {
    staticArea = document.createElement('div');
    staticArea.id = 'static-area';
    staticArea.className = cn.staticArea;
    document.body.appendChild(staticArea);
  }
  return staticArea;
}
function createToastContainer() {
  toastId++;
  const toastContainer = document.createElement('div');
  toastContainer.id = `toast-${toastId}`;
  toastContainer.setAttribute('role', 'alert');
  getStaticArea().appendChild(toastContainer);
  return toastContainer;
}
function getToastDuration(timeout) {
  const settingEnabled = getGlobalSettingsService().get(GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION);
  const setting = settingEnabled === 'true' ? getGlobalSettingsService().get(GlobalSettingsKeys.POPUP_DISPLAY_DURATION) : undefined;
  const settingsDisplayDuration = setting ? parseFloat(setting) * 1000 : undefined;
  if (settingsDisplayDuration && timeout && timeout > settingsDisplayDuration || timeout && !settingsDisplayDuration) {
    // Use passed in custom timeout value if custom settings display duration is not set,
    // or if the custom value is longer than the value in the settings
    return timeout;
  } else if (settingsDisplayDuration) {
    // Use value from settings if it is set and longer than any passed in custom timeout value
    return settingsDisplayDuration;
  } else {
    // Otherwise just return the default toast duration
    return DEFAULT_DURATION * 1000;
  }
}
function clearToast(toast) {
  if (!toast) {
    return;
  }
  clearTimeout(toast.timeout);
  const staticArea = getStaticArea();
  if (staticArea.contains(toast.container)) {
    const animationEndHandler = () => {
      toast.root.unmount();
      staticArea.removeChild(toast.container);
      toast.toast?.removeEventListener('animationend', animationEndHandler);
    };
    const closingAnimation = toast.toast?.dataset.closingAnim;
    if (toast.hideAnimation || !closingAnimation || closingAnimation === 'false') {
      animationEndHandler();
    } else {
      toast.toast?.addEventListener('animationend', animationEndHandler);
      toast.toast?.classList.add(closingAnimation);
    }
  }
  if (toast === openToast) {
    openToast = null;
  }
}
/**
 * Imperatively renders a Toast on the page. The toast will close itself.
 *
 * @param message The message to display in a toast.
 * @param options
 * @param options.closeable When true the toast will display a close button.
 * @param options.displayMode A string the specifies a predefined style to use for the toast.
 */

export const useShowMessageToast = () => {
  const themeContext = useThemeContext();
  const globalDesignTokens = useGlobalDesignTokens();
  const writingMode = useWritingModeContext();
  const directSpeech = useDirectSpeech({
    mode: 'assertive'
  });
  const useTextLocalizer = createUseTextLocalizer(bundle);
  const localize = useTextLocalizer();
  return function showMessageToast(message, options) {
    const container = createToastContainer();
    clearToast(openToast);
    const actionButtonRef = /*#__PURE__*/createRef();
    const closeButtonRef = /*#__PURE__*/createRef();
    const actionButtonProps = options?.actionButton;

    // Save the currently focused element
    const previouslyFocusedElement = document.activeElement;
    const timeout = window.setTimeout(() => {
      // Ensure backward compatibility: Do not close the toast if the close button or action button is focused
      if (thisToast.hovering || actionButtonRef.current === document.activeElement || closeButtonRef?.current === document.activeElement) {
        thisToast.timeout = -1;
      } else {
        clearToast(thisToast);
      }
    }, getToastDuration(options?.timeout));
    const root = createRoot(container);
    const thisToast = {
      container,
      root,
      timeout,
      hovering: false,
      toast: null,
      hideAnimation: options?.hideAnimation ?? false
    };
    root.render( /*#__PURE__*/_jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx("div", {
        style: globalDesignTokens,
        children: /*#__PURE__*/_jsx(ThemeProvider, {
          ...themeContext,
          children: /*#__PURE__*/_jsx(WritingModeProvider, {
            ...writingMode,
            children: /*#__PURE__*/_jsx(MessageToast, {
              isOpen: true,
              toastRef: element => {
                thisToast.toast = element;

                // Notify via directSpeech after the toast content is rendered
                if (element) {
                  if (options?.actionButton) {
                    setTimeout(() => {
                      if (actionButtonRef.current) {
                        directSpeech?.notify(localize('action.button.available.msg'));
                      }
                    }, 100);
                  }
                  if (options?.closeable) {
                    setTimeout(() => {
                      directSpeech?.notify(localize('close.message.button.available.msg'));
                    }, 200);
                  }
                }
              },
              ...options,
              actionButton: actionButtonProps ? {
                ...actionButtonProps,
                ref: actionButtonRef,
                onBlur: () => {
                  if (thisToast.timeout === -1) clearToast(thisToast);
                }
              } : undefined,
              onMouseEnter: () => {
                thisToast.hovering = true;
              },
              onMouseLeave: () => {
                thisToast.hovering = false;
                if (thisToast.timeout === -1 && actionButtonRef.current !== document.activeElement && closeButtonRef.current !== document.activeElement) clearToast(thisToast);
              },
              closeable: options?.closeable,
              onClose: () => {
                clearToast(thisToast);
                options?.onClose?.();

                // Restore focus to the previously focused element if it exists
                if (previouslyFocusedElement && previouslyFocusedElement.focus) {
                  previouslyFocusedElement.focus();
                }
              },
              closeButtonRef: closeButtonRef,
              children: message
            })
          })
        })
      })
    }));
    openToast = thisToast;
  };
};
//# sourceMappingURL=MessageToaster.js.map